import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { Button, CheckIcon, Image, Typography } from '@iheartjane/reefer';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { Form } from 'react-router';

import { WEIGHT_TO_GRAMS } from '@jane/bloom/dataAccess/constants';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import type { ShouldShowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

import { WeightButtons } from './weightButtons';

import styles from './selectWeightForm.module.css';

interface SelectWeightFormProps {
  customRowId?: number;
  maxWeightOption?: number | null;
  onSubmit: (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => void;
  onSuccess?: () => void;
  product: ProductSearchResultViewModel;
  smartProductInstance?: SmartSortProduct<AlgoliaProduct>;
  weightFilter?: ShouldShowWeightFilter;
}

export function SelectWeightForm({
  customRowId,
  maxWeightOption,
  onSubmit,
  onSuccess,
  product,
  smartProductInstance,
  weightFilter,
}: SelectWeightFormProps) {
  const filteredBySpecial =
    weightFilter && smartProductInstance
      ? product.available_weights.filter((weight) =>
          weightFilter(weight, smartProductInstance)
        )
      : product.available_weights;

  const hasWeightLimit =
    maxWeightOption !== null && maxWeightOption !== undefined;

  const availableWeights = hasWeightLimit
    ? filteredBySpecial.filter(
        (weight) => WEIGHT_TO_GRAMS[weight] <= maxWeightOption
      )
    : filteredBySpecial;

  const [selectedWeight, setSelectedWeight] = useState<string>('');
  const {
    fetcher: { state },
    formArgs,
  } = useAddToCartConfig({
    product,
    productId: product.product_id,
    quantity: 1,
    rowId: customRowId,
    selectedWeight,
  });
  const isAddingToCart = state === 'submitting';

  const selectedWeightPotency = selectedWeight
    ? Object.values(product.availableWeightsFormatted).find(
        (entry) => entry.formattedWeight === selectedWeight
      )?.potencyLabResults
    : null;
  const displayedPotency = selectedWeightPotency ?? product.potencyLabResults;

  useEffect(() => {
    if (state === 'loading') {
      onSuccess?.();
    }
  }, [state, onSuccess]);

  return (
    <>
      <div className={styles['productCardFormInfo']}>
        <Image
          altText=""
          src={product.image_urls[0]}
          srcSet={product.imageSrcSet?.srcSet}
          height="96px"
          width="96px"
        />
        <div>
          <Typography color="text-main" variant="body-semibold">
            {product.name}
          </Typography>
          <Typography color="text-light">
            {product.brand} {product.root_subtype}
          </Typography>
          {displayedPotency && (
            <Typography color="text-light" maxLines={1}>
              {displayedPotency}
            </Typography>
          )}
          {product.dosageAmountPackInfo && (
            <Typography color="text-light" maxLines={1}>
              {product.dosageAmountPackInfo}
            </Typography>
          )}
        </div>
      </div>

      <Typography
        className={styles['productCardFormWeightsTitle']}
        color="text-main"
        variant="body-semibold"
        mt={40}
        mb={16}
      >
        Select weight
      </Typography>
      <div className={styles['productCardFormWeights']}>
        {availableWeights.map((weight: string) => {
          const formattedWeight = product.availableWeightsFormatted?.[weight];
          return (
            <WeightButtons
              key={weight}
              product={product}
              formattedWeight={formattedWeight}
              weight={weight}
              selectedWeight={selectedWeight}
              setSelectedWeight={setSelectedWeight}
              smartProductInstance={smartProductInstance}
            />
          );
        })}
        <Form
          name={`${product.id}-add-to-cart`}
          className={styles['productCardAddToCartForm']}
          onSubmit={onSubmit}
          {...formArgs}
        >
          <input type="hidden" name="productId" value={product.id} />
          <input type="hidden" name="priceId" value={selectedWeight} />
          <input type="hidden" name="count" value={1} />

          <Button
            alignment="center"
            startIcon={
              isAddingToCart && (
                <CheckIcon className={styles['textContrastColor']} />
              )
            }
            disabled={
              !selectedWeight ||
              isAddingToCart ||
              (hasWeightLimit &&
                WEIGHT_TO_GRAMS[selectedWeight] > maxWeightOption)
            }
            variant="primary"
            full
            label="Add to bag"
            data-testid="add-to-cart-multiple-weights"
            type="submit"
          />
        </Form>
      </div>
    </>
  );
}
